import React, { useState, useEffect, useRef } from 'react';
import rawHeroData from '../../data/hero.json';
import type { HeroData, HeroProps } from './hero.types';
import './Hero.styles.css';

const heroData = rawHeroData as HeroData;
const TARGET_NAME = heroData.name;
const CHAR_DELAY_MS = 260;

// * [COMPONENT] Hero Introduction Section
// ? Decoupled data architecture, slow deliberate typewriter, dynamic rem eagle talon perch anchor,
// ? tactical crosshair reticle HUD, top-left pre-tag HUD, and kinetic velocity tag runner.
export const Hero: React.FC<HeroProps> = ({
  className = '',
  scale = 1,
}) => {
  // * Typewriter animation state tracking - plays smoothly on view, letter-by-letter
  const [isTypingComplete, setIsTypingComplete] = useState<boolean>(false);
  const [charIndex, setCharIndex] = useState<number>(0);

  // * Responsive screen width tracking for scale transform origin
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  const typeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // * Tactical Perched Crosshair State
  // ? Initially locked on eagle's eye; as soon as cursor moves, unmounts from eagle eye while GlobalCrosshairCursor tracks globally
  const [hasUserMovedCursor, setHasUserMovedCursor] = useState<boolean>(false);

  // * Kinetic Velocity Tag Runner State
  const [activeTagIndex, setActiveTagIndex] = useState<number>(0);
  const [tagPhase, setTagPhase] = useState<'entering' | 'holding' | 'exiting'>('holding');
  const tagTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // * Execute slow 260ms deliberate typewriter sequence; strictly terminate upon completion
  useEffect(() => {
    if (isTypingComplete) return;

    const typeNextChar = () => {
      setCharIndex((current) => {
        const next = current + 1;
        if (next >= TARGET_NAME.length) {
          setIsTypingComplete(true);
          return TARGET_NAME.length;
        }
        typeTimerRef.current = setTimeout(typeNextChar, CHAR_DELAY_MS);
        return next;
      });
    };

    // Initial buffer (500ms) before typing initiates
    typeTimerRef.current = setTimeout(typeNextChar, 300);

    return () => {
      if (typeTimerRef.current) {
        clearTimeout(typeTimerRef.current);
      }
    };
  }, [isTypingComplete]);

  // * Kinetic single-tag velocity rotation cycle (hold -> exit right with trails -> enter left -> hold)
  useEffect(() => {
    if (!isTypingComplete || !heroData.badges.length) return;

    let isMounted = true;

    const runCycle = () => {
      // Phase 1: Hold stationary for readability (1800ms)
      tagTimerRef.current = setTimeout(() => {
        if (!isMounted) return;
        setTagPhase('exiting');

        // Phase 2: Exit to right with velocity trails (350ms)
        tagTimerRef.current = setTimeout(() => {
          if (!isMounted) return;
          setActiveTagIndex((prev) => (prev + 1) % heroData.badges.length);
          setTagPhase('entering');

          // Phase 3: Enter from left rapidly into holding position (350ms)
          tagTimerRef.current = setTimeout(() => {
            if (!isMounted) return;
            setTagPhase('holding');
            runCycle();
          }, 350);
        }, 350);
      }, 1800);
    };

    runCycle();

    return () => {
      isMounted = false;
      if (tagTimerRef.current) {
        clearTimeout(tagTimerRef.current);
      }
    };
  }, [isTypingComplete]);

  // * Window mousemove listener: removes perched crosshair from eagle eye once user moves cursor
  useEffect(() => {
    const handleMouseMove = () => {
      setHasUserMovedCursor(true);
    };

    window.addEventListener('mousemove', handleMouseMove, { once: true, passive: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // * Break name string into prefix and perched trailing anchor ("a.")
  // ? TARGET_NAME is "Yash Haldiya." -> prefix is "Yash Haldiy", anchor is "a."
  const splitIndex = Math.max(0, TARGET_NAME.length - 2);
  const prefixTarget = TARGET_NAME.slice(0, splitIndex);
  const anchorTarget = TARGET_NAME.slice(splitIndex);

  const displayedPrefix = charIndex <= splitIndex
    ? prefixTarget.slice(0, charIndex)
    : prefixTarget;

  const displayedAnchor = charIndex > splitIndex
    ? anchorTarget.slice(0, charIndex - splitIndex)
    : '';

  return (
    <section className={`hero-viewport ${className}`}>
      {/* * Floating Top-Left Pre-Tag Tactical HUD */}
      <div className="pre-tag-hud" role="note" aria-label="System telemetry">
        <span className="pre-tag-indicator" aria-hidden="true" />
        <span className="pre-tag-label">{heroData.preTag}</span>
      </div>

      {/* * Primary Content Container: Strict zero-background policy with dynamic viewport scaling */}
      <div
        className="hero-content"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: isMobile ? 'left top' : 'left center',
          transition: 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        {/* * Hero Heading with Typewriter */}
        <h1 className="hero-heading">
          <span className="heading-prefix">{displayedPrefix}</span>

          {/* * Trailing characters */}
          {(displayedAnchor.length > 0 || isTypingComplete) && (
            <span className="talon-anchor">
              {displayedAnchor}
            </span>
          )}

          {/* * Monospace Pipe Cursor (permanently unmounted upon typing completion) */}
          {!isTypingComplete && (
            <span className="typewriter-cursor" aria-hidden="true">
              |
            </span>
          )}
        </h1>

        {/* * Subtext and Kinetic Velocity Tag Runner */}
        <div className={`hero-sub-block ${isTypingComplete ? 'revealed' : ''}`}>
          <p className="hero-subtext">{heroData.subtext}</p>
          <div className="kinetic-tag-track">
            <div className={`kinetic-tag-runner phase-${tagPhase}`}>
              <span className="tag-trail-echo" aria-hidden="true">
                {heroData.badges[activeTagIndex]}
              </span>
              <span className="tag-label">
                {heroData.badges[activeTagIndex]}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* * Tactical Optical Sensor Crosshair Reticle */}
      {/* ? Locks onto eagle's eye upon typewriter completion; removed as soon as user starts moving cursor */}
      {isTypingComplete && !hasUserMovedCursor && (
        <div className="eagle-eye-crosshair perched-eye" aria-hidden="true">
          <div className="crosshair-swirl-container">
            <div className="crosshair-axis-x" />
            <div className="crosshair-axis-y" />
            <div className="crosshair-ring-outer" />
            <div className="crosshair-ring-inner" />
            <div className="crosshair-bracket top-left" />
            <div className="crosshair-bracket top-right" />
            <div className="crosshair-bracket bottom-left" />
            <div className="crosshair-bracket bottom-right" />
            <div className="crosshair-point" />
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
